import TeamMatch from '@/models/TeamMatch';
import MatchSide from '@/models/MatchSide';
import { TeamMatchFormat } from '@/services/utils/enums';
import { translate as i18nTranslate } from '@/i18n';

class TeamMatchBuilder {
  /**
   * Группирует Game[] по teamMatchKey и собирает доменные TeamMatch.
   * @param {Array<import('@/models/Game').default>} [games=[]]
   * @param {Object} [options={}]
   * @returns {TeamMatch[]}
   */
  build(games = [], options = {}) {
    const {
      teamMatchFormat = TeamMatchFormat.BOARD_SUM,
      teams = [],
      players = [],
      translate = null,
    } = options;

    const grouped = new Map();

    for (const game of games) {
      const teamMatchKey = game?.getTeamMatchKey?.();
      if (!teamMatchKey) continue;

      if (!grouped.has(teamMatchKey)) {
        grouped.set(teamMatchKey, []);
      }
      grouped.get(teamMatchKey).push(game);
    }

    return Array.from(grouped.entries())
      .map(([teamMatchKey, teamGames]) => {
        const firstGame = teamGames[0];
        const side1 = this.#buildSide(firstGame, 'white', { teams, players, games: teamGames, translate });
        const side2 = this.#buildSide(firstGame, 'black', { teams, players, games: teamGames, translate });
        const status = this.#buildStatus(teamGames);
        const isByeGroup = teamGames.some((game) => game?.isByeGroupGame?.());

        return TeamMatch.fromObject({
          teamMatchKey,
          round: firstGame?.round ?? null,
          stage: firstGame?.stage ?? null,
          stageNumber: firstGame?.stageNumber ?? null,
          bracketIndex: firstGame?.bracketIndex ?? null,
          side1,
          side2,
          games: teamGames,
          teamMatchFormat,
          status,
          meta: {
            category: firstGame?.category ?? null,
            isByeGroup,
          },
        });
      })
      .sort((a, b) => {
        if ((a.round ?? 0) !== (b.round ?? 0)) return (a.round ?? 0) - (b.round ?? 0);
        if ((a.stageNumber ?? 0) !== (b.stageNumber ?? 0)) return (a.stageNumber ?? 0) - (b.stageNumber ?? 0);
        return (a.bracketIndex ?? 0) - (b.bracketIndex ?? 0);
      });
  }

  /**
   * Собирает сторону командного матча из teamContext первой Game.
   * @param {import('@/models/Game').default} game
   * @param {'white'|'black'} sideColor
   * @param {Object} [options={}]
   * @returns {MatchSide}
   */
  #buildSide(game, sideColor, { teams = [], players = [], games = [], translate = null } = {}) {
    const isWhite = sideColor === 'white';
    const teamId = isWhite ? game?.getWhiteTeamId?.() : game?.getBlackTeamId?.();
    const teamKey = isWhite ? game?.getWhiteTeamKey?.() : game?.getBlackTeamKey?.();

    if (teamId) {
      const teamEntity = teams.find((team) => team.id === teamId) ?? null;
      const playerIds = this.#getTeamIdPlayerIds(teamId, games);
      const members = this.#resolvePlayers(playerIds, { teams, players, game });
      return MatchSide.fromObject({
        entityType: 'team',
        id: teamId,
        name: teamEntity?.name ?? (typeof translate === 'function'
          ? translate(isWhite ? 'gameAccordion.whiteTeam' : 'gameAccordion.blackTeam')
          : i18nTranslate(isWhite ? 'gameAccordion.whiteTeam' : 'gameAccordion.blackTeam')),
        members,
        entity: teamEntity,
        meta: { teamId, playerIds },
      });
    }

    if (teamKey) {
      const rawTeam = isWhite ? game?.teamContext?.whiteTeam : game?.teamContext?.blackTeam;
      const playerIds = this.#getTeamKeyPlayerIds(teamKey, games, rawTeam);
      const members = this.#resolvePlayers(playerIds, { teams, players, game });
      const normalizedPlayerIds = Array.isArray(playerIds) ? playerIds : [];
      const teamName = this.#buildTeamKeyName(members, rawTeam?.name || teamKey);

      return MatchSide.fromObject({
        entityType: 'team-key',
        id: teamKey,
        name: teamName,
        members,
        entity: {
          ...(rawTeam || {}),
          id: teamKey,
          key: rawTeam?.key ?? teamKey,
          name: teamName,
          playerIds: rawTeam?.playerIds ?? normalizedPlayerIds,
          players: rawTeam?.players ?? members,
          temporary: true,
        },
        meta: {
          teamKey,
          playerIds: normalizedPlayerIds,
        },
      });
    }

    return MatchSide.fromObject({
      entityType: 'team-key',
      id: null,
      name: '',
      members: [],
      entity: null,
      meta: {},
    });
  }

  /**
   * Возвращает id игроков временной команды по ключу.
   * @param {string} teamKey
   * @param {Array<import('@/models/Game').default>} [games=[]]
   * @param {Object|null} [rawTeam=null]
   * @returns {string[]}
   */
  #getTeamKeyPlayerIds(teamKey, games = [], rawTeam = null) {
    const ids = [];
    games.forEach((game) => {
      if (game?.getWhiteTeamKey?.() === teamKey) {
        ids.push(...(Array.isArray(game?.whitePlayerIds) ? game.whitePlayerIds : []));
      }
      if (game?.getBlackTeamKey?.() === teamKey) {
        ids.push(...(Array.isArray(game?.blackPlayerIds) ? game.blackPlayerIds : []));
      }
    });

    const gameIds = [...new Set(ids.filter(Boolean))];
    if (gameIds.length) return gameIds;

    return Array.isArray(rawTeam?.playerIds) ? rawTeam.playerIds : [];
  }

  /**
   * Возвращает id игроков fixed-команды по фактически записанным сторонам Game.
   * Это важно для замен: базовый `team.players` может содержать игрока, который
   * уже вышел на замену в другой participant-слот.
   * @param {string} teamId
   * @param {Array<import('@/models/Game').default>} [games=[]]
   * @returns {string[]}
   */
  #getTeamIdPlayerIds(teamId, games = []) {
    const ids = [];

    games.forEach((game) => {
      if (game?.getWhiteTeamId?.() === teamId) {
        ids.push(...(game.getSideEntries?.('white') ?? []).map((entry) => entry?.playerId));
      }
      if (game?.getBlackTeamId?.() === teamId) {
        ids.push(...(game.getSideEntries?.('black') ?? []).map((entry) => entry?.playerId));
      }
    });

    return [...new Set(ids.filter(Boolean))];
  }

  /**
   * Формирует имя временной команды из имён игроков.
   * @param {Array<Object>} [members=[]]
   * @param {string} fallback
   * @returns {string}
   */
  #buildTeamKeyName(members = [], fallback) {
    const names = members.map((player) => player?.name).filter(Boolean);
    return names.length ? names.join('/') : fallback;
  }

  /**
   * Разворачивает id игроков во внешние Player-объекты из доступных источников.
   * @param {string[]} [playerIds=[]]
   * @param {Object} [options={}]
   * @returns {Array<Object>}
   */
  #resolvePlayers(playerIds = [], { teams = [], players = [], game = null } = {}) {
    if (!Array.isArray(playerIds) || !playerIds.length) return [];

    const teamPlayers = teams.flatMap((team) => Array.isArray(team?.players) ? team.players : []);
    const gamePlayers = [
      ...(Array.isArray(game?.teamContext?.whiteTeam?.players) ? game.teamContext.whiteTeam.players : []),
      ...(Array.isArray(game?.teamContext?.blackTeam?.players) ? game.teamContext.blackTeam.players : []),
    ];
    const candidates = [...players, ...teamPlayers, ...gamePlayers];

    return playerIds
      .map((playerId) => candidates.find((player) => player?.id === playerId) ?? { id: playerId, name: 'unknown' })
      .filter(Boolean);
  }

  /**
   * Определяет статус TeamMatch по результатам и окончаниям внутренних Game.
   * @param {Array<import('@/models/Game').default>} games
   * @returns {'pending'|'ongoing'|'finished'}
   */
  #buildStatus(games) {
    const hasStarted = games.some((game) => Boolean(game?.result || game?.termination));
    const isFinished = games.length > 0 && games.every((game) => Boolean(game?.result || game?.termination));

    if (isFinished) return 'finished';
    if (hasStarted) return 'ongoing';
    return 'pending';
  }
}

export default new TeamMatchBuilder();
